/**
 * Active Listing Stage Cache Service
 * Fetches the Active Listing template from the backend API and caches it per
 * licence state. State-aware (used to be flat-cached; broke TX LISTING render
 * once the backend grew per-state stage lists — see `ActiveListingStageDefinitions.java`):
 *   - CA / CO → 5-stage uniform flow (Add MLS ID → … → Process and generate timelines)
 *   - TX      → 12-stage spec starting at Confirm Effective Date
 *   - DEFAULT (any state outside SUPPORTED_STATES) → 10-stage DEFAULT spec
 *
 * Mirrors mobile's activeListingStageCache.service.ts.
 */

import { apiClient, API_ENDPOINTS } from '../config/api';

// Per-state cache so a TX agent and a CA agent on the same device don't clobber
// each other's lists when switching between deals.
const cache = {};
const inFlight = {};

const normalizeState = (state) =>
  (state ?? '').toString().trim().toUpperCase() || 'CA';

/**
 * Fetch the Active Listing stage template for the given state from the backend.
 * Safe to call multiple times — concurrent calls for the same state are deduped
 * and only run once unless force === true.
 */
export const loadActiveListingStages = (state, force = false) => {
  const key = normalizeState(state);
  if (cache[key] && !force) return Promise.resolve();
  if (inFlight[key]) return inFlight[key];

  inFlight[key] = (async () => {
    try {
      const token = localStorage.getItem('firebaseToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await apiClient.get(API_ENDPOINTS.activeListingStages, {
        headers,
        params: { state: key },
      });
      const data = response.data;

      if (Array.isArray(data?.stages)) {
        cache[key] = data.stages;
        console.log(`[ActiveListingStageCache] Loaded ${cache[key].length} stages for state=${key}`);
      } else {
        console.warn(`[ActiveListingStageCache] Unexpected API response shape for state=${key}, stages unavailable`);
      }
    } catch (error) {
      console.warn(`[ActiveListingStageCache] Failed to load stages for state=${key}:`, error?.message || error);
    } finally {
      delete inFlight[key];
    }
  })();

  return inFlight[key];
};

/** Whether the stage template for the given state has been successfully loaded. */
export const areActiveListingStagesLoaded = (state) => cache[normalizeState(state)] !== undefined;

/** Ensure the stage template for the given state is loaded. Triggers a fetch if not cached yet. */
export const ensureActiveListingStagesLoaded = (state) => loadActiveListingStages(state);

/** Get the cached stage template for a given state. Synchronous — returns [] until first successful load. */
export const getCachedActiveListingStages = (state) => cache[normalizeState(state)] ?? [];

/** Get display metadata (name/description/roleLabel) for a given stage id within a given state. */
export const getActiveListingStageMeta = (state, stageId) =>
  cache[normalizeState(state)]?.find((s) => s.id === stageId);
